import { create } from "zustand";
import type {
  BillingSessionState,
  LineItem,
  CustElement,
  LaneElement,
} from "../types/billing";
import {
  emptyInvoice,
  computeTotals,
  updateInvoiceItems,
} from "../lib/billing-helpers";

// --- Store interface ---

type AddItemPayload = {
  materialNumber: string;
  description: string;
  price: number;
  stock: number;
};

interface BillingStore extends BillingSessionState {
  visibleCount: number;
  setVisibleCount: (count: number) => void;
  init: (invoiceCount: number) => void;
  addItem: (payload: AddItemPayload) => void;
  setItem: (item: LineItem, top?: boolean) => void;
  removeItem: (materialNumber: string) => void;
  setQuantity: (materialNumber: string, qty: number) => void;
  setCustomer: (customer: CustElement) => void;
  clearCustomer: () => void;
  reset: (resetLane?: boolean) => void;
  switchInvoice: () => void;
  setActiveIndex: (index: number) => void;
  setLane: (lane: LaneElement, clearItems: boolean) => void;
  setExtraDiscountPercent: (value: number) => void;
  setRoundoff: (value: number) => void;
  setSplitEligible: (value: boolean) => void;
  setSplitEnabled: (value: boolean) => void;
  reconcile: (index: number, lineItems: LineItem[]) => void;
}

export const useBillingStore = create<BillingStore>()((set, get) => ({
  invoices: [{ ...emptyInvoice }, { ...emptyInvoice }],
  activeIndex: 0,
  visibleCount: 1,

  setVisibleCount: (count: number) => set({ visibleCount: count }),

  init: (invoiceCount: number) => {
    const { invoices } = get();
    // Only reinitialise if the count changed — preserve existing state
    if (invoices.length !== invoiceCount) {
      const newInvoices = Array.from({ length: invoiceCount }, (_, i) =>
        invoices[i] ? { ...invoices[i] } : { ...emptyInvoice },
      );
      set({
        invoices: newInvoices,
        activeIndex: Math.min(get().activeIndex, invoiceCount - 1),
      });
    }
  },

  addItem: (payload) => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    const { materialNumber, description, price, stock } = payload;
    const existing = active.lineItems.find(
      (li) => li.materialNumber === materialNumber,
    );

    let newItems: LineItem[];
    if (existing) {
      newItems = active.lineItems.map((li) =>
        li.materialNumber === materialNumber
          ? {
              ...li,
              quantity: li.quantity + 1,
              amount: (li.quantity + 1) * li.price - li.discount,
            }
          : li,
      );
    } else {
      const item: LineItem = {
        id: materialNumber,
        materialNumber,
        description,
        printDescription: description,
        quantity: 1,
        price,
        stock,
        discount: 0,
        amount: price,
        category: "",
        subCategory: "",
        materialType: "",
        brandName: "",
        brandItem: "",
        packageSize: "",
        packageType: "",
        hsnCode: "",
        taxRate: 0,
        cess: 0,
        additionalCess: 0,
        purchasePrice: 0,
        aroed: "",
        currentStock: stock,
        offer: 0,
        discountType: "",
        calculationType: "",
        discountPercent: 0,
        discountQuantity: 0,
        offerCategory: 0,
        offerType: 0,
        weightedAveragePrice: 0,
        grossAmount: 0,
        discountAmount: 0,
        taxAmount: 0,
        cessAmount: 0,
        additionalCessAmount: 0,
        netAmount: price,
        extraDiscount: 0,
        showDiscount: 0,
        discountFlag: "N",
      };
      newItems = [...active.lineItems, item];
    }

    const updated = updateInvoiceItems(active, newItems);
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = updated;
    set({ invoices: newInvoices });
  },

  setItem: (item, top = false) => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    const idx = active.lineItems.findIndex(
      (li) => li.materialNumber === item.materialNumber,
    );
    let newItems: LineItem[];
    if (idx >= 0) {
      if (top) {
        newItems = [
          item,
          ...active.lineItems.filter(
            (li) => li.materialNumber !== item.materialNumber,
          ),
        ];
      } else {
        newItems = [...active.lineItems];
        newItems[idx] = item;
      }
    } else {
      newItems = top
        ? [item, ...active.lineItems]
        : [...active.lineItems, item];
    }
    const updated = updateInvoiceItems(active, newItems);
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = updated;
    set({ invoices: newInvoices });
  },

  removeItem: (materialNumber) => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    const newItems = active.lineItems.filter(
      (li) => li.materialNumber !== materialNumber,
    );
    const updated = updateInvoiceItems(active, newItems);
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = updated;
    set({ invoices: newInvoices });
  },

  setQuantity: (materialNumber, qty) => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    if (qty <= 0) {
      const newItems = active.lineItems.filter(
        (li) => li.materialNumber !== materialNumber,
      );
      const updated = updateInvoiceItems(active, newItems);
      const newInvoices = [...invoices];
      newInvoices[activeIndex] = updated;
      set({ invoices: newInvoices });
      return;
    }
    const newItems = active.lineItems.map((li) =>
      li.materialNumber === materialNumber
        ? {
            ...li,
            quantity: qty,
            amount: qty * li.price - li.discount,
          }
        : li,
    );
    const updated = updateInvoiceItems(active, newItems);
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = updated;
    set({ invoices: newInvoices });
  },

  setCustomer: (customer) => {
    const { activeIndex, invoices } = get();
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = { ...invoices[activeIndex], customer };
    set({ invoices: newInvoices });
  },

  clearCustomer: () => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    const cleared = {
      ...active,
      customer: null,
      extraDiscountPercent: 0,
      extraDiscountAmount: 0,
      roundoff: 0,
    };
    const totals = computeTotals(cleared.lineItems, 0, 0);
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = { ...cleared, ...totals };
    set({ invoices: newInvoices });
  },

  reset: (resetLane = false) => {
    const { activeIndex, invoices } = get();
    const lane = resetLane ? null : invoices[activeIndex].lane;
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = { ...emptyInvoice, lane };
    set({ invoices: newInvoices });
  },

  switchInvoice: () => {
    const { activeIndex, invoices } = get();
    set({ activeIndex: (activeIndex + 1) % invoices.length });
  },

  setActiveIndex: (index) => {
    const { invoices } = get();
    if (index >= 0 && index < invoices.length) {
      set({ activeIndex: index });
    }
  },

  setLane: (lane, clearItems) => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    const newInvoices = [...invoices];
    if (clearItems) {
      newInvoices[activeIndex] = {
        ...emptyInvoice,
        lane,
        customer: active.customer,
      };
    } else {
      newInvoices[activeIndex] = { ...active, lane };
    }
    set({ invoices: newInvoices });
  },

  setExtraDiscountPercent: (value) => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    const totals = computeTotals(active.lineItems, value, active.roundoff);
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = {
      ...active,
      extraDiscountPercent: value,
      ...totals,
    };
    set({ invoices: newInvoices });
  },

  setRoundoff: (value) => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    const totals = computeTotals(
      active.lineItems,
      active.extraDiscountPercent,
      value,
    );
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = { ...active, roundoff: value, ...totals };
    set({ invoices: newInvoices });
  },

  setSplitEligible: (value) => {
    const { activeIndex, invoices } = get();
    const active = invoices[activeIndex];
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = {
      ...active,
      splitEligible: value,
      splitEnabled: value ? active.splitEnabled : false,
    };
    set({ invoices: newInvoices });
  },

  setSplitEnabled: (value) => {
    const { activeIndex, invoices } = get();
    const newInvoices = [...invoices];
    newInvoices[activeIndex] = {
      ...invoices[activeIndex],
      splitEnabled: value,
    };
    set({ invoices: newInvoices });
  },

  reconcile: (index, lineItems) => {
    const { invoices } = get();
    if (index < 0 || index >= invoices.length) return;
    const updated = updateInvoiceItems(invoices[index], lineItems);
    const newInvoices = [...invoices];
    newInvoices[index] = updated;
    set({ invoices: newInvoices });
  },
}));
